import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useFeatureFlags } from '@shared/flags/FeatureFlagProvider';
import { Building2, Users, Mail, UserPlus, Crown, Trash2, Database, X, User, ArrowRight, Image as ImageIcon, Pencil, Check, Sparkles, Globe, Megaphone } from 'lucide-react';
import { useOrganizations, type Organization } from '../hooks/useOrganizations';
import Chip from '../components/Chip';
import Button from '../components/Button';
import ConfirmationDialog from '../components/ConfirmationDialog';
import CustomFieldsPanel from '../components/organization-settings/CustomFieldsPanel';
import BrandingTab from '../components/organization-settings/BrandingTab';
import AIFeaturesTab from '../components/organization-settings/AIFeaturesTab';
import SignupCodesCard from '../components/organization-settings/SignupCodesCard';
import PublicProfileCard from '../components/organization-settings/PublicProfileCard';
import PublicPositionsTab from '../components/organization-settings/PublicPositionsTab';
import OrgEventsTab from '../components/organization-settings/OrgEventsTab';
import { Palette, CalendarDays } from 'lucide-react';
import SearchField from '../components/SearchField';
import { api } from '../App';
import toast from 'react-hot-toast';
import Tabs from '@shared/ui/Tabs';
import Modal from '@shared/ui/Modal';
import PageShell from '../components/PageShell';
import { PeopleIcon, UploadIcon } from '../components/icons';
import { StandardTable, StandardTableHeader, StandardTableHeaderCell, StandardTableBody, StandardTableRow, StandardTableCell } from '../components/StandardTable';

interface StaffMember {
  id: string;
  user_id: string;
  email: string;
  display_name: string;
  first_name: string;
  last_name: string;
  role: string;
  member_type: string;
  status: string;
  created_at: string;
}

interface PendingInvitation {
  id: string;
  email: string;
  role: string;
  status: string;
  expires_at: string;
  created_at: string;
  invited_by: string;
  invited_by_name: string;
}

interface SearchedUser {
  id: string;
  username: string;
  display_name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  organization_name?: string;
}

interface OrganizationInfo {
  id: string;
  name: string;
  subscription: string;
  email?: string;
  contact?: string;
  website?: string;
  phone?: string;
  logoUrl?: string;
  logoFile?: string;
  timezone?: string;
  created_at: string;
}

/** Tab set for the settings page. Ids are stable — they appear in ?tab=. */
const ORG_SETTINGS_TABS = [
  { id: 'details', label: 'Organization Details', icon: Building2 },
  { id: 'staff', label: 'Staff Management', icon: Users },
  { id: 'custom-fields', label: 'Custom Fields', icon: Database },
  { id: 'branding', label: 'Branding', icon: Palette },
  { id: 'ai-features', label: 'AI Features', icon: Sparkles },
  { id: 'public-positions', label: 'Public Positions', icon: Megaphone },
  { id: 'events', label: 'Events', icon: CalendarDays },
];

/*
  Outreach Branding and Report Branding used to be two tabs over two tables.
  They are one Branding tab now; Reports is a surface inside it, and appears
  only for orgs flagged into next-gen-reporting (the /api/ngr routes are
  flag-gated, and reports are not a thing an unflagged org has).

  Old deep links keep working: ?tab=outreach-branding and ?tab=report-branding
  both resolve to the merged tab.
*/
const LEGACY_TAB_ALIASES: Record<string, string> = {
  'outreach-branding': 'branding',
  'report-branding': 'branding',
};

/*
  A user can be org_admin in several orgs. The page used to take the first one
  (by name) with no way to reach the rest, so the org being managed is now
  explicit: ?org=<id>, falling back to the first admin org when absent or not
  one the user administers. The dropdown only renders with 2+ admin orgs.
*/
const OrganizationSettingsPage: React.FC = () => {
  const { organizations, loading, refetch } = useOrganizations();
  const [searchParams, setSearchParams] = useSearchParams();

  const adminOrgs = organizations.filter(org => org.role?.toLowerCase() === 'org_admin');
  const requestedOrgId = searchParams.get('org');
  const currentOrg = adminOrgs.find(org => org.id === requestedOrgId) ?? adminOrgs[0];

  const handleSelectOrg = (orgId: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('org', orgId);
    setSearchParams(next);
  };

  // Initial load only: refetch() after a rename/logo change also flips
  // `loading`, and blanking the page then would unmount the content mid-edit.
  if (loading && organizations.length === 0) {
    return (
      <div className="space-y-6">
          <div className="flex items-center justify-center h-64">
            <div className="text-lg text-text-primary dark:text-text-dark-primary">Loading organization settings...</div>
          </div>
      </div>
    );
  }

  if (!currentOrg) {
    return (
      <div className="space-y-6">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Building2 className="w-16 h-16 mx-auto text-text-tertiary dark:text-text-dark-tertiary mb-4" />
              <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary mb-2">
                No Organization Access
              </h3>
              <p className="text-text-secondary dark:text-text-dark-secondary">
                You need to be an organization admin to access these settings.
              </p>
            </div>
          </div>
      </div>
    );
  }

  // Keyed by org id so every piece of per-org state (staff, invitations, the
  // name being edited, each tab's own fetches) starts fresh on a switch.
  return (
    <OrganizationSettingsContent
      key={currentOrg.id}
      currentOrg={currentOrg}
      adminOrgs={adminOrgs}
      onSelectOrg={handleSelectOrg}
      refetchOrganizations={refetch}
    />
  );
};

interface OrganizationSettingsContentProps {
  currentOrg: Organization;
  adminOrgs: Organization[];
  onSelectOrg: (orgId: string) => void;
  refetchOrganizations: () => Promise<void>;
}

const OrganizationSettingsContent: React.FC<OrganizationSettingsContentProps> = ({
  currentOrg,
  adminOrgs,
  onSelectOrg,
  refetchOrganizations,
}) => {
  const { flags } = useFeatureFlags();
  const reportsEnabled = Boolean(flags['next-gen-reporting']);
  const settingsTabs = ORG_SETTINGS_TABS;
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab') || 'details';
  const [activeTab, setActiveTab] = useState(
    LEGACY_TAB_ALIASES[requestedTab] || requestedTab
  );
  const [staffMembers, setStaffMembers] = useState<StaffMember[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<PendingInvitation[]>([]);
  const [organizationInfo, setOrganizationInfo] = useState<OrganizationInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [showCancelInviteDialog, setShowCancelInviteDialog] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<StaffMember | null>(null);
  const [invitationToCancel, setInvitationToCancel] = useState<PendingInvitation | null>(null);
  const [inviteForm, setInviteForm] = useState({
    email: '',
    role: 'org_admin'
  });

  // Search-first invite modal state
  const [inviteStep, setInviteStep] = useState<'search' | 'email'>('search');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchedUser[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState<SearchedUser | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Logo upload state
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isDeletingLogo, setIsDeletingLogo] = useState(false);

  // Organization name editing state
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [isSavingName, setIsSavingName] = useState(false);

  // Timezone editing state
  const [isSavingTimezone, setIsSavingTimezone] = useState(false);

  // Handle tab changes from URL parameters
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    // Derived from the tab set rather than a second hand-maintained list, which
    // had already drifted: 'public-positions' was missing, so ?tab=public-positions
    // was silently ignored.
    const resolved = LEGACY_TAB_ALIASES[tabParam || ''] || tabParam;
    if (resolved && ORG_SETTINGS_TABS.some((tab) => tab.id === resolved)) {
      setActiveTab(resolved);
    }
  }, [searchParams]);

  useEffect(() => {
    if (currentOrg) {
      fetchOrganizationData().catch(e => console.error('fetchOrganizationData failed:', e));
      fetchStaffMembers().catch(e => console.error('fetchStaffMembers failed:', e));
      fetchPendingInvitations().catch(e => console.error('fetchPendingInvitations failed:', e));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg]);

  // Debounced search for users
  useEffect(() => {
    if (!showInviteModal || !currentOrg) return;

    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    // Searching from the first keystroke, not after the debounce: otherwise
    // the empty result list reads "No users found" for 300 ms. A response
    // for a query that has since changed is ignored.
    let cancelled = false;
    setIsSearching(true);
    const timeoutId = setTimeout(async () => {
      try {
        const response = await api.get(`/organizations/${currentOrg.id}/search-users?q=${encodeURIComponent(searchQuery)}`);
        if (!cancelled) setSearchResults(response.data.users || []);
      } catch (error: any) {
        if (cancelled) return;
        console.error('Search error:', error);
        toast.error(error.response?.data?.message || 'Failed to search users');
        setSearchResults([]);
      } finally {
        if (!cancelled) setIsSearching(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [searchQuery, currentOrg, showInviteModal]);

  const fetchOrganizationData = async () => {
    try {
      const response = await api.get(`/organizations/${currentOrg?.id}`);
      if (response.data.success) {
        setOrganizationInfo(response.data.data);
      } else {
        throw new Error('Failed to fetch organization data');
      }
    } catch (error) {
      console.error('Failed to fetch organization data:', error);
      // Set a fallback organization info based on what we know
      setOrganizationInfo({
        id: currentOrg?.id || '',
        name: currentOrg?.name || 'Organization',
        subscription: currentOrg?.subscription || 'none',
        created_at: new Date().toISOString()
      });
    }
  };

  const fetchStaffMembers = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(`/organizations/${currentOrg?.id}/staff`);
      setStaffMembers(response.data);
    } catch (error) {
      console.error('Failed to fetch staff members:', error);
      // Don't show error toast, just gracefully handle by showing empty state
      setStaffMembers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPendingInvitations = async () => {
    try {
      const response = await api.get(`/organizations/${currentOrg?.id}/pending-invitations`);
      setPendingInvitations(response.data || []);
    } catch (error) {
      console.error('Failed to fetch pending invitations:', error);
      // Don't show error toast, just gracefully handle by showing empty state
      setPendingInvitations([]);
    }
  };

  const handleCancelInvitation = async () => {
    if (!invitationToCancel || !currentOrg) return;

    try {
      setIsSubmitting(true);
      await api.delete(`/organizations/${currentOrg.id}/invitations/${invitationToCancel.id}`);
      toast.success('Invitation cancelled successfully');
      setShowCancelInviteDialog(false);
      setInvitationToCancel(null);
      fetchPendingInvitations();
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to cancel invitation';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };


  const handleInviteStaff = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!inviteForm.email.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteForm.email.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }

    try {
      setIsSubmitting(true);
      await api.post(`/organizations/${currentOrg?.id}/invite-staff`, {
        email: inviteForm.email.trim(),
        role: inviteForm.role
      });

      toast.success('Organization admin invited successfully!');
      handleCloseInviteModal();
      fetchStaffMembers();
      fetchPendingInvitations();
    } catch (error: any) {
      const message = error.response?.data?.error || error.response?.data?.message || 'Failed to send invitation';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddSelectedUser = async () => {
    if (!selectedUser || !currentOrg) return;

    try {
      setIsSubmitting(true);
      await api.post(`/organizations/${currentOrg.id}/invite-staff`, {
        userId: selectedUser.id,
        role: 'org_admin'
      });

      toast.success('Organization admin added successfully!');
      handleCloseInviteModal();
      fetchStaffMembers();
    } catch (error: any) {
      const message = error.response?.data?.error || error.response?.data?.message || 'Failed to add admin';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSelectUser = (user: SearchedUser) => {
    setSelectedUser(user);
  };

  const handleProceedToEmail = () => {
    setInviteStep('email');
    setSelectedUser(null);
  };

  const handleBackToSearch = () => {
    setInviteStep('search');
    setInviteForm({ email: '', role: 'org_admin' });
  };

  const handleCloseInviteModal = () => {
    setShowInviteModal(false);
    setInviteStep('search');
    setSearchQuery('');
    setSearchResults([]);
    setSelectedUser(null);
    setInviteForm({ email: '', role: 'org_admin' });
  };

  const getUserDisplayName = (user: SearchedUser) => {
    if (user.display_name) return user.display_name;
    if (user.first_name || user.last_name) {
      return `${user.first_name || ''} ${user.last_name || ''}`.trim();
    }
    return user.username;
  };

  const handleRemoveStaff = async () => {
    if (!memberToRemove || !currentOrg) return;

    try {
      setIsSubmitting(true);
      await api.delete(`/organizations/${currentOrg.id}/staff/${memberToRemove.user_id}`);
      toast.success('Staff member removed successfully');
      setShowRemoveDialog(false);
      setMemberToRemove(null);
      fetchStaffMembers();
    } catch (error: any) {
      const message = error.response?.data?.error || error.response?.data?.message || 'Failed to remove staff member';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !currentOrg) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Only PNG, JPG, JPEG, SVG, and WebP are allowed.');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size too large. Maximum size is 5MB.');
      return;
    }

    try {
      setIsUploadingLogo(true);
      const formData = new FormData();
      formData.append('logo', file);

      await api.put(`/organizations/${currentOrg.id}/upload-logo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('Logo uploaded successfully');
      // Refresh organization data to get the new logo URL
      await fetchOrganizationData();
      // Refresh organizations list to update navigation pane
      await refetchOrganizations();
      // Trigger organization logo update event for Layout component
      window.dispatchEvent(new Event('organizationLogoUpdated'));
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to upload logo';
      toast.error(message);
    } finally {
      setIsUploadingLogo(false);
      // Clear the file input
      event.target.value = '';
    }
  };

  const handleLogoDelete = async () => {
    if (!currentOrg) return;

    if (!window.confirm('Are you sure you want to delete the organization logo?')) {
      return;
    }

    try {
      setIsDeletingLogo(true);
      await api.delete(`/organizations/${currentOrg.id}/logo`);
      toast.success('Logo deleted successfully');
      // Refresh organization data to clear the logo URL
      await fetchOrganizationData();
      // Refresh organizations list to update navigation pane
      await refetchOrganizations();
      // Trigger organization logo update event for Layout component
      window.dispatchEvent(new Event('organizationLogoUpdated'));
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to delete logo';
      toast.error(message);
    } finally {
      setIsDeletingLogo(false);
    }
  };

  const handleEditName = () => {
    if (organizationInfo) {
      setEditedName(organizationInfo.name);
      setIsEditingName(true);
    }
  };

  const handleCancelEditName = () => {
    setIsEditingName(false);
    setEditedName('');
  };

  const handleSaveName = async () => {
    if (!currentOrg || !editedName.trim()) return;

    try {
      setIsSavingName(true);
      const response = await api.put(`/organizations/${currentOrg.id}`, {
        name: editedName.trim()
      });

      if (response.data.success) {
        toast.success('Organization name updated successfully');
        setOrganizationInfo(prev => prev ? { ...prev, name: editedName.trim() } : null);
        setIsEditingName(false);
        setEditedName('');
        // Refresh organizations list to update navigation pane
        await refetchOrganizations();
      }
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to update organization name';
      toast.error(message);
    } finally {
      setIsSavingName(false);
    }
  };

  const handleTimezoneChange = async (newTimezone: string) => {
    if (!currentOrg || !newTimezone) return;

    try {
      setIsSavingTimezone(true);
      const response = await api.put(`/organizations/${currentOrg.id}`, {
        timezone: newTimezone,
      });

      if (response.data.success) {
        toast.success('Timezone updated successfully');
        setOrganizationInfo(prev => prev ? { ...prev, timezone: newTimezone } : null);
        await refetchOrganizations();
      }
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to update timezone';
      toast.error(message);
    } finally {
      setIsSavingTimezone(false);
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'org_admin': return <Crown className="w-4 h-4" />;
      case 'contributor': return <Users className="w-4 h-4" />;
      case 'guest': return <PeopleIcon className="w-4 h-4" />;
      default: return <Users className="w-4 h-4" />;
    }
  };

  const getRoleVariant = (role: string): 'success' | 'info' | 'neutral' => {
    switch (role) {
      case 'org_admin': return 'success';
      case 'contributor': return 'info';
      case 'guest': return 'neutral';
      default: return 'neutral';
    }
  };

  const orgPicker = adminOrgs.length > 1 ? (
    <label className="flex items-center gap-2 text-sm text-text-secondary dark:text-text-dark-secondary">
      <span className="whitespace-nowrap">Organization</span>
      <select
        value={currentOrg.id}
        onChange={(e) => onSelectOrg(e.target.value)}
        className="input min-w-[16rem]"
        aria-label="Organization to manage"
      >
        {adminOrgs.map((org) => (
          <option key={org.id} value={org.id}>{org.name}</option>
        ))}
      </select>
    </label>
  ) : undefined;

  // Show loading for staff data
  if (isLoading) {
    return (
      <div className="space-y-6">
          <div className="flex items-center justify-center h-64">
            <div className="text-lg text-text-primary dark:text-text-dark-primary">Loading organization settings...</div>
          </div>
      </div>
    );
  }

  return (
    <PageShell
      title="Organization Settings"
      description="Manage your organization's details, staff, and custom fields"
      actions={orgPicker}
    >

        <div className="space-y-6">
          {/* Tab Navigation */}
          <div className="bg-background dark:bg-background-dark-secondary">
            <Tabs
              level="section"
              ariaLabel="Organization settings sections"
              tabs={settingsTabs}
              activeId={activeTab}
              onChange={setActiveTab}
            />
          </div>

          {/* Public Positions Tab Content (citizen-facing picks — roadmap Phase 2) */}
          {activeTab === 'public-positions' && (organizationInfo?.id || currentOrg?.id) && (
            <PublicPositionsTab organizationId={(organizationInfo?.id || currentOrg?.id) as string} />
          )}

          {/* Org Events Tab Content (calendar Phase 2 — visibility ladder) */}
          {activeTab === 'events' && (organizationInfo?.id || currentOrg?.id) && (
            <OrgEventsTab organizationId={(organizationInfo?.id || currentOrg?.id) as string} />
          )}

          {/* Organization Details Tab Content */}
          {activeTab === 'details' && (
            <div className="space-y-6">
              {/* Sign-Up Codes Card */}
              {(organizationInfo?.id || currentOrg?.id) && (
                <SignupCodesCard organizationId={(organizationInfo?.id || currentOrg?.id) as string} />
              )}

              {/* Public profile + citizen-directory discoverability (D-2) */}
              {(organizationInfo?.id || currentOrg?.id) && (
                <PublicProfileCard organizationId={(organizationInfo?.id || currentOrg?.id) as string} />
              )}

              {/* Organization Info Card */}
              {organizationInfo && (
                <div className="card">
                  <div className="flex items-center space-x-2 mb-4">
                    <Building2 className="h-5 w-5 text-primary-700 dark:text-primary-400" />
                    <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">Organization Details</h3>
                  </div>

                  {/* Organization Header with Logo */}
                  <div className="flex items-start gap-6">
                    {/* Logo Section */}
                    <div className="flex-shrink-0">
                      <div className="relative group">
                        {organizationInfo.logoUrl ? (
                          <div className="relative">
                            <img
                              src={organizationInfo.logoUrl}
                              alt={`${organizationInfo.name} logo`}
                              className="w-24 h-24 rounded-lg object-cover border-2 border-border-light dark:border-border-dark"
                            />
                            <button
                              onClick={handleLogoDelete}
                              disabled={isDeletingLogo}
                              className="absolute -top-2 -right-2 p-1.5 bg-red-600 dark:bg-red-700 text-white rounded-full hover:bg-red-700 dark:hover:bg-red-800 disabled:opacity-50 disabled:cursor-not-allowed shadow-md transition-colors"
                              title="Delete logo"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ) : (
                          <div className="w-24 h-24 rounded-lg bg-background-tertiary dark:bg-background-dark-tertiary border-2 border-dashed border-border-light dark:border-border-dark flex items-center justify-center">
                            <ImageIcon className="w-8 h-8 text-text-tertiary dark:text-text-dark-tertiary" />
                          </div>
                        )}

                        {/* Upload Button */}
                        <label className="mt-2 inline-flex items-center justify-center gap-2 font-medium rounded-lg whitespace-nowrap transition-colors shadow-sm px-3 py-1.5 text-sm bg-primary-700 text-white hover:bg-primary-800 cursor-pointer">
                          <UploadIcon className="w-4 h-4" aria-hidden="true" />
                          {isUploadingLogo ? 'Uploading…' : 'Upload'}
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                            onChange={handleLogoUpload}
                            disabled={isUploadingLogo}
                            className="hidden"
                          />
                        </label>
                        <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-1 text-center">
                          Max 5MB
                        </p>
                      </div>
                    </div>

                    {/* Organization Info */}
                    <div className="flex-1">
                      {/* Organization Name - Editable */}
                      <div className="mb-6">
                        <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
                          Organization Name
                        </label>
                        {isEditingName ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={editedName}
                              onChange={(e) => setEditedName(e.target.value)}
                              className="input flex-1"
                              placeholder="Enter organization name"
                              autoFocus
                            />
                            <Button
                              variant="primary"
                              size="sm"
                              icon={Check}
                              onClick={handleSaveName}
                              disabled={isSavingName || !editedName.trim()}
                            >
                              {isSavingName ? 'Saving...' : 'Save'}
                            </Button>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={handleCancelEditName}
                              disabled={isSavingName}
                            >
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-3">
                            <h4 className="text-xl font-semibold text-text-primary dark:text-text-dark-primary">
                              {organizationInfo.name}
                            </h4>
                            <button
                              onClick={handleEditName}
                              className="p-1.5 text-text-tertiary hover:text-primary-600 dark:text-text-dark-tertiary dark:hover:text-primary-400 rounded-md hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary transition-colors"
                              title="Edit organization name"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Additional Info */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-text-secondary dark:text-text-dark-secondary">
                        {organizationInfo.email && (
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4" />
                            {organizationInfo.email}
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          {staffMembers.length} Staff Members
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Timezone Setting Card */}
              {organizationInfo && (
                <div className="card">
                  <div className="flex items-center space-x-2 mb-4">
                    <Globe className="h-5 w-5 text-primary-700 dark:text-primary-400" />
                    <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">Timezone</h3>
                  </div>
                  <p className="text-sm text-text-secondary dark:text-text-dark-secondary mb-3">
                    Exported reports use this timezone. On-screen dates and times display in each viewer&apos;s local timezone.
                  </p>
                  <div className="max-w-md">
                    <select
                      value={organizationInfo.timezone || 'UTC'}
                      onChange={(e) => handleTimezoneChange(e.target.value)}
                      disabled={isSavingTimezone}
                      className="input w-full"
                    >
                      <optgroup label="US Timezones">
                        <option value="America/New_York">Eastern Time (America/New_York)</option>
                        <option value="America/Chicago">Central Time (America/Chicago)</option>
                        <option value="America/Denver">Mountain Time (America/Denver)</option>
                        <option value="America/Los_Angeles">Pacific Time (America/Los_Angeles)</option>
                        <option value="America/Anchorage">Alaska Time (America/Anchorage)</option>
                        <option value="Pacific/Honolulu">Hawaii Time (Pacific/Honolulu)</option>
                      </optgroup>
                      <optgroup label="Other">
                        <option value="UTC">UTC</option>
                        {(() => {
                          try {
                            const supportedValuesOf = (Intl as Record<string, unknown>).supportedValuesOf as
                              ((key: string) => string[]) | undefined;
                            if (!supportedValuesOf) return null;
                            const allTimezones = supportedValuesOf('timeZone');
                            const usTimezones = new Set([
                              'America/New_York', 'America/Chicago', 'America/Denver',
                              'America/Los_Angeles', 'America/Anchorage', 'Pacific/Honolulu', 'UTC'
                            ]);
                            return allTimezones
                              .filter((tz: string) => !usTimezones.has(tz))
                              .map((tz: string) => (
                                <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>
                              ));
                          } catch {
                            return null;
                          }
                        })()}
                      </optgroup>
                    </select>
                    {isSavingTimezone && (
                      <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-1">Saving...</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Staff Management Tab Content */}
          {activeTab === 'staff' && (
            <div className="space-y-6">
              {/* Staff Members Card */}
              <div className="card">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <Users className="h-5 w-5 text-primary-700 dark:text-primary-400" />
                    <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">Staff Members</h3>
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    icon={UserPlus}
                    onClick={() => setShowInviteModal(true)}
                  >
                    Invite admin
                  </Button>
                </div>

                {/* Pending Invitations Section */}
                {pendingInvitations.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-3">
                      Pending Invitations ({pendingInvitations.length})
                    </h4>
                    <div className="space-y-3">
                      {pendingInvitations.map((invitation) => (
                        <div
                          key={invitation.id}
                          className="flex items-center justify-between p-4 bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800/30 rounded-lg"
                        >
                          <div className="flex items-center space-x-4">
                            <div className="w-10 h-10 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center text-sm font-medium text-yellow-700 dark:text-yellow-400">
                              <Mail className="w-5 h-5" />
                            </div>
                            <div>
                              <h4 className="font-medium text-text-primary dark:text-text-dark-primary">
                                {invitation.email}
                              </h4>
                              <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                                Invited by {invitation.invited_by_name} • Expires {new Date(invitation.expires_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            <Chip
                              label="PENDING"
                              variant="warning"
                              size="sm"
                            />
                            <button
                              onClick={() => {
                                setInvitationToCancel(invitation);
                                setShowCancelInviteDialog(true);
                              }}
                              className="p-2 text-text-tertiary dark:text-text-dark-tertiary hover:text-error-600 dark:hover:text-error-400 hover:bg-error-50 dark:hover:bg-error-900/20 rounded-lg transition-colors"
                              title="Cancel invitation"
                              aria-label="Cancel invitation"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Staff Members Section */}
                {staffMembers.length === 0 && pendingInvitations.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="w-12 h-12 text-text-tertiary dark:text-text-dark-tertiary mx-auto mb-4" />
                    <h4 className="text-lg font-medium text-text-primary dark:text-text-dark-primary mb-2">
                      No staff members yet
                    </h4>
                    <p className="text-text-secondary dark:text-text-dark-secondary mb-4">
                      Start by inviting your first staff member to the organization.
                    </p>
                    <Button
                      variant="primary"
                      size="sm"
                      icon={UserPlus}
                      onClick={() => setShowInviteModal(true)}
                    >
                      Invite Organization Admin
                    </Button>
                  </div>
                ) : staffMembers.length > 0 && (
                  <div className="space-y-4">
                    <h4 className="text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
                      Active Staff Members ({staffMembers.length})
                    </h4>
                    <StandardTable>
                      <StandardTableHeader>
                        <StandardTableRow>
                          <StandardTableHeaderCell columnType="expand">Member</StandardTableHeaderCell>
                          <StandardTableHeaderCell columnType="fit">Role</StandardTableHeaderCell>
                          <StandardTableHeaderCell columnType="actions" align="right">Actions</StandardTableHeaderCell>
                        </StandardTableRow>
                      </StandardTableHeader>
                      <StandardTableBody>
                        {staffMembers.map((member) => (
                          <StandardTableRow key={member.id}>
                            <StandardTableCell>
                              <div className="flex items-center space-x-4">
                                <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-sm font-medium text-primary-700 dark:text-primary-400">
                                  {member.display_name
                                    ? member.display_name.charAt(0).toUpperCase()
                                    : member.email?.charAt(0)?.toUpperCase() || '?'
                                  }
                                </div>
                                <div>
                                  <div className="font-medium text-text-primary dark:text-text-dark-primary">
                                    {member.display_name || `${member.first_name} ${member.last_name}` || 'Unknown User'}
                                  </div>
                                  <div className="text-sm text-text-secondary dark:text-text-dark-secondary">
                                    {member.email}
                                  </div>
                                </div>
                              </div>
                            </StandardTableCell>
                            <StandardTableCell>
                              <div className="flex items-center space-x-2">
                                {getRoleIcon(member.role)}
                                <Chip
                                  label={member.role.replace('_', ' ').toUpperCase()}
                                  variant={getRoleVariant(member.role)}
                                  size="sm"
                                />
                              </div>
                            </StandardTableCell>
                            <StandardTableCell align="right">
                              {/* Remove is hidden only for the last remaining org_admin */}
                              {!(staffMembers.filter(m => m.role?.toLowerCase() === 'org_admin').length <= 1 && member.role?.toLowerCase() === 'org_admin') && (
                                <button
                                  onClick={() => {
                                    setMemberToRemove(member);
                                    setShowRemoveDialog(true);
                                  }}
                                  className="p-2 text-text-tertiary dark:text-text-dark-tertiary hover:text-error-600 dark:hover:text-error-400 hover:bg-error-50 dark:hover:bg-error-900/20 rounded-lg transition-colors"
                                  title="Remove staff member"
                                  aria-label="Remove staff member"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </StandardTableCell>
                          </StandardTableRow>
                        ))}
                      </StandardTableBody>
                    </StandardTable>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Custom Fields Tab Content */}
          {activeTab === 'custom-fields' && currentOrg && (
            <div className="card">
              <CustomFieldsPanel
                organizationId={currentOrg.id}
                organizationName={currentOrg.name}
              />
            </div>
          )}

          {/* Branding Tab Content — one brand for every surface, with
              per-surface overrides for orgs that want them. */}
          {activeTab === 'branding' && currentOrg && (
            <div className="card">
              <BrandingTab
                organizationId={currentOrg.id}
                organizationName={currentOrg.name}
                reportsEnabled={reportsEnabled}
              />
            </div>
          )}

          {/* AI Features Tab Content */}
          {activeTab === 'ai-features' && currentOrg && (
            <div className="card">
              <AIFeaturesTab
                organizationId={currentOrg.id}
              />
            </div>
          )}
        </div>

      {/* Invite Organization Admin Modal */}
      {showInviteModal && (
        <Modal
          isOpen={true}
          title={
            inviteStep === 'search'
              ? 'Invite organization administrator'
              : 'Send email invitation'
          }
          onClose={handleCloseInviteModal}
          size="sm"
          /* Two steps with different actions; the email step keeps its own
             <form> and reaches this submit by id. */
          footer={
            inviteStep === 'search' ? (
              <Button variant="secondary" onClick={handleCloseInviteModal}>
                Cancel
              </Button>
            ) : (
              <>
                <Button variant="secondary" onClick={handleCloseInviteModal}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  form="invite-org-admin"
                  variant="primary"
                  loading={isSubmitting}
                  disabled={isSubmitting}
                >
                  Send invitation
                </Button>
              </>
            )
          }
        >
              {inviteStep === 'search' ? (
                <>
                  <p className="text-sm text-text-secondary dark:text-text-dark-secondary mb-4">
                    Organization admins can manage workspaces, invite members, and configure organization settings.
                  </p>

                  {/* Search Section */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
                        Search for existing user
                      </label>
                      <SearchField
                        value={searchQuery}
                        onChange={setSearchQuery}
                        placeholder="Search by name, username, or email..."
                        className="w-full"
                      />
                      <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-1">
                        Search for users to add as organization admin
                      </p>
                    </div>

                    {/* Search Results */}
                    {searchQuery.trim().length >= 2 && (
                      <div className="border border-border-light dark:border-border-dark rounded-lg overflow-hidden">
                        {isSearching ? (
                          <div className="p-4 text-center text-text-secondary dark:text-text-dark-secondary">
                            Searching...
                          </div>
                        ) : searchResults.length > 0 ? (
                          <div className="divide-y divide-border-light dark:divide-border-dark">
                            {searchResults.map((user) => (
                              <button
                                key={user.id}
                                onClick={() => handleSelectUser(user)}
                                className={`w-full p-4 text-left hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary transition-colors ${selectedUser?.id === user.id ? 'bg-primary-50 dark:bg-primary-900/20 border-l-4 border-primary-600' : ''
                                  }`}
                              >
                                <div className="flex items-center space-x-3">
                                  <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center">
                                    <User className="w-5 h-5 text-primary-700 dark:text-primary-300" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-text-primary dark:text-text-dark-primary truncate">
                                      {getUserDisplayName(user)}
                                    </p>
                                    <p className="text-sm text-text-secondary dark:text-text-dark-secondary truncate">
                                      @{user.username} {user.email && `• ${user.email}`}
                                    </p>
                                    {user.organization_name && (
                                      <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary truncate mt-0.5">
                                        {user.organization_name}
                                      </p>
                                    )}
                                  </div>
                                  {selectedUser?.id === user.id && (
                                    <div className="flex-shrink-0">
                                      <div className="w-6 h-6 bg-primary-600 rounded-full flex items-center justify-center">
                                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                        </svg>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </button>
                            ))}
                          </div>
                        ) : (
                          <div className="p-4 text-center text-text-secondary dark:text-text-dark-secondary">
                            No users found matching &quot;{searchQuery}&quot;
                          </div>
                        )}
                      </div>
                    )}

                    {/* Selected User Actions */}
                    {selectedUser && (
                      <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4 space-y-4">
                        <div className="bg-background-tertiary dark:bg-background-dark-tertiary p-3 rounded-md">
                          <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                            <strong className="text-text-primary dark:text-text-dark-primary">Role:</strong> Organization Admin
                          </p>
                          <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-1">
                            Full admin access to all workspaces and organization settings
                          </p>
                        </div>

                        <button
                          onClick={handleAddSelectedUser}
                          disabled={isSubmitting}
                          className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                        >
                          {isSubmitting ? 'Adding Admin...' : `Add ${getUserDisplayName(selectedUser)} as Admin`}
                        </button>
                      </div>
                    )}

                    {/* Divider */}
                    <div className="relative py-4">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-border-light dark:border-border-dark"></div>
                      </div>
                      <div className="relative flex justify-center">
                        <span className="px-4 bg-white dark:bg-background-dark-secondary text-sm text-text-secondary dark:text-text-dark-secondary">
                          or
                        </span>
                      </div>
                    </div>

                    {/* Proceed to Email Invitation */}
                    <button
                      onClick={handleProceedToEmail}
                      className="w-full flex items-center justify-center space-x-2 px-4 py-3 border border-border-light dark:border-border-dark rounded-lg hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary transition-colors text-text-primary dark:text-text-dark-primary"
                    >
                      <Mail className="w-5 h-5" />
                      <span>Invite by Email Instead</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* Email Invitation Form */}
                  <form id="invite-org-admin" onSubmit={handleInviteStaff} className="space-y-4">
                    <button
                      type="button"
                      onClick={handleBackToSearch}
                      className="text-sm text-primary-600 dark:text-primary-400 hover:underline flex items-center space-x-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                      </svg>
                      <span>Back to search</span>
                    </button>

                    <div>
                      <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={inviteForm.email}
                        onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                        placeholder="admin@example.com"
                        className="input"
                        required
                      />
                      <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-1">
                        Invitee will receive an email to join as organization admin
                      </p>
                    </div>

                    <div className="bg-background-tertiary dark:bg-background-dark-tertiary p-3 rounded-md">
                      <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                        <strong className="text-text-primary dark:text-text-dark-primary">Role:</strong> Organization Admin
                      </p>
                      <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-1">
                        Full admin access to all workspaces and organization settings
                      </p>
                    </div>

                  </form>
                </>
              )}
        </Modal>
      )}

      {/* Remove Staff Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showRemoveDialog}
        onCancel={() => {
          setShowRemoveDialog(false);
          setMemberToRemove(null);
        }}
        onConfirm={handleRemoveStaff}
        title="Remove Staff Member"
        message={`Are you sure you want to remove ${memberToRemove?.display_name || memberToRemove?.email} from the organization? They will lose access to all organization workspaces.`}
        confirmText={isSubmitting ? 'Removing...' : 'Remove'}
        cancelText="Cancel"
      />

      {/* Cancel Invitation Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showCancelInviteDialog}
        onCancel={() => {
          setShowCancelInviteDialog(false);
          setInvitationToCancel(null);
        }}
        onConfirm={handleCancelInvitation}
        title="Cancel Invitation"
        message={`Are you sure you want to cancel the invitation for ${invitationToCancel?.email}? They will no longer be able to join the organization using this invitation.`}
        confirmText={isSubmitting ? 'Cancelling...' : 'Cancel Invitation'}
        cancelText="Keep Invitation"
      />
    </PageShell>
  );
};

export default OrganizationSettingsPage;